import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { LiveNowBanner } from "@/components/LiveNowBanner";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useSections } from "@/lib/useSections";
import { useContent } from "@/context/SiteContentContext";
import { useAuth } from "@/context/AuthContext";
import { Heart, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { EditorsLetterStrip } from "@/components/EditorsLetterStrip";

const RoomCard = ({ s, count, isFavorite, onToggleFavorite, onPost, favoritesBusy }) => {
  const navigate = useNavigate();
  return (
    <div
      data-testid={`room-card-${s.slug}`}
      className="card-belle-hover"
      style={{
        display: "block",
        background: "var(--surface)",
        border: "1px solid var(--border-default)",
        color: "var(--text-primary)",
        position: "relative",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(`/room/${s.slug}`)}
        aria-label={`Enter ${s.label}`}
        data-testid={`room-enter-${s.slug}`}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "block",
          width: "100%",
          color: "var(--text-primary)",
        }}
      >
        <div className="media-zoom" style={{ aspectRatio: "5/4", overflow: "hidden" }}>
          <img src={s.image} alt={s.label} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div style={{ padding: 28 }}>
          <div className="overline" style={{ color: s.accent }}>{s.overline}</div>
          <h2 className="font-serif" style={{ fontSize: 30, marginTop: 8, marginBottom: 12, fontWeight: 500, lineHeight: 1.15 }}>{s.label}</h2>
          <p className="editorial-body" style={{ fontSize: 14.5, margin: 0 }}>{s.description}</p>
          <div style={{ marginTop: 18, paddingBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="overline">{count ?? 0} {count === 1 ? "post" : "posts"}</span>
            <span
              data-testid={`room-enter-label-${s.slug}`}
              style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--brand-accent)", fontWeight: 500 }}
            >
              Enter here →
            </span>
          </div>
        </div>
      </button>

      {/* Heart toggle — top-right of the card */}
      <button
        type="button"
        data-testid={`room-heart-${s.slug}`}
        onClick={(e) => { e.stopPropagation(); onToggleFavorite(s.slug); }}
        disabled={favoritesBusy}
        aria-label={isFavorite ? `Remove ${s.label} from favorites` : `Add ${s.label} to favorites`}
        title={isFavorite ? "Favorite room — click to remove" : "Add to my favorite rooms"}
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "none",
          background: "rgba(0, 0, 0, 0.42)",
          color: "#FAF9F5",
          display: "grid",
          placeItems: "center",
          cursor: favoritesBusy ? "wait" : "pointer",
          backdropFilter: "blur(4px)",
          transition: "all 0.2s ease",
        }}
      >
        <Heart
          size={18}
          strokeWidth={1.6}
          fill={isFavorite ? s.accent : "none"}
          color={isFavorite ? s.accent : "#FAF9F5"}
        />
      </button>

      {/* "+ Post here" — bottom-right. Consistent burgundy across all rooms
          so the CTA never blends into the room image. */}
      <button
        type="button"
        data-testid={`room-post-here-${s.slug}`}
        onClick={(e) => { e.stopPropagation(); onPost(s.slug); }}
        aria-label={`Post to ${s.label}`}
        title={`Post a photo or video to ${s.label}`}
        style={{
          position: "absolute",
          bottom: 22,
          right: 22,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          fontSize: 10.5,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 600,
          borderRadius: 24,
          border: "1px solid var(--brand-accent)",
          background: "var(--brand-accent)",
          color: "#FAF9F5",
          cursor: "pointer",
        }}
      >
        <Plus size={12} strokeWidth={2} /> Post here
      </button>
    </div>
  );
};

const HEART_HINT_KEY = "salon.rooms.heart_hint_dismissed_v1";

const Rooms = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [favoritesBusy, setFavoritesBusy] = useState(false);
  const [showHeartHint, setShowHeartHint] = useState(false);
  const sections = useSections();
  const heroOverline = useContent("rooms.hero.overline", "The Salon · Rooms");
  const heroTitle = useContent("rooms.hero.title", "Seven rooms.\nSeven conversations.");
  const heroBody = useContent(
    "rooms.hero.body",
    "Each is its own small magazine — with its own editor's pick at the top, and a quiet, growing collection of posts below."
  );

  useEffect(() => {
    api.get("/sections").then(({ data }) => {
      setData(data);
      setLoading(false);
    });
    if (user) {
      api.get("/auth/me").then(({ data }) => {
        const favs = data?.favorite_rooms || [];
        setFavorites(favs);
        try {
          const dismissed = localStorage.getItem(HEART_HINT_KEY) === "1";
          setShowHeartHint(!dismissed && favs.length === 0);
        } catch { /* ignore */ }
      }).catch(() => {});
    }
  }, [user]);

  const dismissHeartHint = () => {
    setShowHeartHint(false);
    try { localStorage.setItem(HEART_HINT_KEY, "1"); } catch { /* ignore */ }
  };

  const toggleFavorite = async (slug) => {
    if (!user) {
      toast.error("Please sign in to favorite rooms.");
      return;
    }
    const next = favorites.includes(slug)
      ? favorites.filter((f) => f !== slug)
      : [...favorites, slug];
    setFavorites(next);
    if (next.length > 0) dismissHeartHint(); // auto-hide once they've favorited anything
    setFavoritesBusy(true);
    try {
      const meResp = await api.get("/auth/me");
      await api.put("/users/me/favorite-rooms", {
        favorite_rooms: next,
        rooms_digest_email: meResp.data?.rooms_digest_email !== false,
      });
    } catch (err) {
      toast.error("Couldn't save. Please try again.");
      setFavorites(favorites); // rollback
    } finally {
      setFavoritesBusy(false);
    }
  };

  const goPost = (slug) => navigate(`/compose?room=${encodeURIComponent(slug)}`);

  const counts = Object.fromEntries(data.map((s) => [s.slug, s.post_count]));

  const row1 = sections.slice(0, 3);
  const row2 = sections.slice(3, 6);
  const row3 = sections.slice(6, 7);

  return (
    <div data-testid="rooms-page" className="app-shell">
      <Navbar />
      <LiveNowBanner />

      <div className="container-belle" style={{ paddingTop: 130, paddingBottom: 64 }}>
        <EditorsLetterStrip />

        <div style={{ marginBottom: 40, maxWidth: 760 }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>{heroOverline}</div>
          <h1 className="editorial-h1" style={{ fontSize: "clamp(40px, 5.5vw, 64px)", marginTop: 14, whiteSpace: "pre-line" }}>
            {heroTitle}
          </h1>
          <p className="editorial-body" style={{ fontSize: 18, marginTop: 18 }}>
            {heroBody}
          </p>
        </div>

        {/* Dismissible heart hint — only until a user favorites their first room */}
        {user && showHeartHint && (
          <div
            data-testid="rooms-heart-hint"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 16px",
              marginBottom: 32,
              background: "var(--bg-alt)",
              borderLeft: "2px solid var(--brand-accent)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5 }}>
              <Heart size={14} strokeWidth={1.8} color="var(--brand-accent)" fill="var(--brand-accent)" />
              <span>
                Step into a room to read what&apos;s inside — or tap <em>Post here</em> to share your first moment.
              </span>
            </div>
            <button
              type="button"
              data-testid="rooms-heart-hint-dismiss"
              onClick={dismissHeartHint}
              aria-label="Dismiss hint"
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                display: "inline-grid", placeItems: "center", padding: 4,
                color: "var(--text-muted)",
              }}
            >
              <X size={16} strokeWidth={1.6} />
            </button>
          </div>
        )}

        {loading && <p style={{ color: "var(--text-muted)" }}>Loading…</p>}

        <div className="rooms-rows" style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          <div className="rooms-row" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32 }}>
            {row1.map((s) => (
              <RoomCard
                key={s.slug} s={s} count={counts[s.slug]}
                isFavorite={favorites.includes(s.slug)}
                onToggleFavorite={toggleFavorite}
                onPost={goPost}
                favoritesBusy={favoritesBusy}
              />
            ))}
          </div>

          <div className="rooms-row" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 32 }}>
            {row2.map((s) => (
              <RoomCard
                key={s.slug} s={s} count={counts[s.slug]}
                isFavorite={favorites.includes(s.slug)}
                onToggleFavorite={toggleFavorite}
                onPost={goPost}
                favoritesBusy={favoritesBusy}
              />
            ))}
          </div>

          {row3.length > 0 && (
            <div
              className="rooms-row-centered"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr minmax(0, calc((100% - 64px) / 3)) 1fr",
                gap: 32,
                alignItems: "start",
              }}
            >
              <div />
              {row3.map((s) => (
                <RoomCard
                  key={s.slug} s={s} count={counts[s.slug]}
                  isFavorite={favorites.includes(s.slug)}
                  onToggleFavorite={toggleFavorite}
                  onPost={goPost}
                  favoritesBusy={favoritesBusy}
                />
              ))}
              <div />
            </div>
          )}
        </div>
      </div>
      <Footer />

      <style>{`
        @media (max-width: 900px) {
          .rooms-row, .rooms-row-centered {
            grid-template-columns: 1fr !important;
          }
          .rooms-row-centered > div:empty { display: none !important; }
        }
        @media (min-width: 901px) and (max-width: 1199px) {
          .rooms-row {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .rooms-row-centered {
            grid-template-columns: 1fr minmax(0, 50%) 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Rooms;
